import { IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faChevronDown, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import moment from 'moment'
import { dateFilterFn } from "src/utils/era-package/filters";

export const useColumns = ({ expandedRows, onToggleExpand } = {}) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'expand',
          header: () => null,
          cell: ({ row }) => {
            const pga = row.original.pga;
            if (!pga || pga.length === 0) return null;
            const isExpanded = expandedRows?.has(row.original.abiTransId);
            return (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); if (onToggleExpand) onToggleExpand(row.original.abiTransId); }}
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} style={{ width: 12, height: 12 }} />
              </IconButton>
            );
          },
          meta: { style: { width: 40, padding: '4px 8px' } },
          enableColumnFilter: false,
        }),
        columnHelper.accessor('entryNumber', {
          header: () => 'Entry Number',
          cell: (info) => (
            <strong>
              {info.getValue()}
            </strong>
          ),
          filterFn: 'includesString',
          meta: {
            priority: 1,
            style: { whiteSpace: 'nowrap' }
          }
        }),
        columnHelper.accessor('referenceNo', {
          header: () => 'My Ref No',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('bl', {
          header: () => 'B/L Number',
          filterFn: 'includesString',
          meta: { priority: 2 },
        }),
        columnHelper.accessor((row) => row['3461Status'], {
          id: '3461Status',
          header: () => '3461',
          filterFn: 'equals',
          meta: {
            priority: 2,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['7501Status'], {
          id: '7501Status',
          header: () => '7501',
          filterFn: 'equals',
          meta: {
            priority: 2,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['1usg'], {
          id: '1usg',
          header: () => '1USG',
          cell: (info) => {
            const val = info.getValue();
            if (!val) return '-';
            return <Typography variant="body2" whiteSpace="nowrap">{val}</Typography>;
          },
          filterFn: 'equals',
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['1c'], {
          id: '1c',
          header: () => '1C',
          cell: (info) => info.getValue() || '-',
          filterFn: 'equals',
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['3z'], {
          id: '3z',
          header: () => '3Z',
          cell: (info) => info.getValue() || '-',
          filterFn: 'equals',
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor('pga', {
          header: () => 'PGA',
          cell: (info) => {
            const pga = info.getValue();
            if (!pga || pga.length === 0) return '-';
            const statuses = [...new Set(pga.map((p) => p.status))];
            return <Typography variant="body2">{statuses.length > 1 ? 'M' : statuses[0]}</Typography>;
          },
          enableColumnFilter: false,
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
          },
        }),
        columnHelper.accessor('entryPort', {
          header: () => 'Port',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('eta', {
          header: () => 'ETA',
          cell: (info) => {
            const date = info.getValue();
            if (!date) return '-';
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <FontAwesomeIcon icon={faCalendar} style={{ width: 16, height: 16 }} />
                <Typography variant="body2" whiteSpace='nowrap'>{moment(new Date(date)).format("LL")}</Typography>
              </Stack>
            );
          },
          filterFn: dateFilterFn,
          meta: { priority: 2 },
        }),
        columnHelper.accessor('statementDate', {
          header: () => 'Statement Date',
          cell: (info) => {
            const date = info.getValue();
            if (!date) return '-';
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <FontAwesomeIcon icon={faCalendar} style={{ width: 16, height: 16 }} />
                <Typography variant="body2" whiteSpace='nowrap'>{moment(new Date(date)).format("LL")}</Typography>
              </Stack>
            );
          },
          filterFn: dateFilterFn,
          meta: { priority: 4, hideOnMobile: isMobile, hideOnTablet: isTablet },
        }),
        columnHelper.accessor('importer', {
          header: () => 'Importer',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('consignee', {
          header: () => 'Consignee',
          filterFn: 'includesString',
          meta: { priority: 4, hideOnMobile: isMobile, hideOnTablet: isTablet },
        }),
        columnHelper.accessor('user', {
          header: () => 'User',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('lastModified', {
          header: () => 'Last Modified',
          cell: (info) => {
            const date = info.getValue();
            if (!date) return '-';
            return new Date(date).toLocaleString()
          },
          enableColumnFilter: false,
          meta: { priority: 5, hideOnMobile: isMobile, hideOnTablet: isTablet },
        }),
      ].filter((column) => {
        if (isSmall && column.meta?.hideOnMobile) return false;
        if (isTablet && column.meta?.hideOnTablet) return false;
        return true;
      }),
    [columnHelper, isMobile, isTablet, isSmall, expandedRows, onToggleExpand],
  );

  return columns
}